/**
 * InlineSegmentCreator
 *
 * Modal compacto para crear un segmento sin salir del wizard de campañas.
 *
 * En esta versión usa el endpoint MOCK del backend (POST /segments/mock):
 * no requiere OpenAI ni el microservicio chattoquery. Se ve y se siente
 * exactamente como si la IA hubiera armado el filtro — incluso muestra
 * un count "encontrado" plausible — pero todo es local. Suficiente para
 * avanzar end-to-end en el flow de Campañas.
 *
 * Si en el futuro queremos conectar la IA real, basta con cambiar
 * `segmentsApi.createMock(...)` por `segmentsApi.converse(...)` +
 * `.save(...)`.
 */

import { useState } from "react";
import {
  Sparkles,
  X,
  Loader2,
  Users,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { segmentsApi } from "../../segments/api";
import type { Segment } from "../../segments/types";

interface InlineSegmentCreatorProps {
  open: boolean;
  brandId: string;
  onClose: () => void;
  onCreated: (segment: Segment) => void;
}

const EXAMPLE_PROMPTS = [
  "Clientes que compraron más de 3 veces en los últimos 90 días",
  "Usuarios que dejaron carrito abandonado en las últimas 24 horas",
  "Mujeres mayores de 25 con ticket promedio mayor a $5000",
  "Clientes inactivos hace más de 30 días",
];

/**
 * Pseudo-conteo determinístico basado en la longitud y palabras del
 * prompt. Da la sensación de que la IA "calculó" un número en lugar
 * de mostrar siempre algo random. Va a ser entre ~80 y ~1400.
 */
function fakeAudienceCount(description: string): number {
  const base = 200;
  const lenBoost = Math.min(description.length * 3, 600);
  const wordBoost = description.split(/\s+/).length * 25;
  const jitter = Math.floor(Math.random() * 200);
  return base + lenBoost + wordBoost + jitter;
}

export function InlineSegmentCreator({
  open,
  brandId,
  onClose,
  onCreated,
}: InlineSegmentCreatorProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ count: number; summary: string } | null>(
    null,
  );

  if (!open) return null;

  const handleClose = () => {
    if (pending) return;
    setName("");
    setDescription("");
    setError(null);
    setPreview(null);
    onClose();
  };

  /**
   * Paso 1: "armamos" el filtro localmente. No hay llamada al backend
   * todavía — solo damos feedback visual de que la IA está pensando.
   */
  const handlePreview = async () => {
    if (!description.trim()) {
      setError("Describí a tu audiencia para que la IA arme el filtro");
      return;
    }
    setError(null);
    setPending(true);

    // Latencia simulada (la IA "está pensando")
    await new Promise((r) => setTimeout(r, 800));

    setPreview({
      count: fakeAudienceCount(description.trim()),
      summary: description.trim(),
    });
    setPending(false);
  };

  /**
   * Paso 2: persistimos el segmento via POST /segments/mock. El backend
   * crea un Segment.status='saved' con filter vacío y nuestro summary.
   */
  const handleConfirm = async () => {
    if (!preview) {
      setError("Primero generá el filtro con la IA");
      return;
    }
    if (!name.trim()) {
      setError("Ponele un nombre al segmento");
      return;
    }
    setError(null);
    setPending(true);
    try {
      const res = await segmentsApi.createMock({
        brandId,
        name: name.trim(),
        description: preview.summary,
      });
      if (res.success && res.data) {
        // Mantengo el count que mostramos en el preview para coherencia
        // visual con lo que el usuario acaba de ver.
        const segmentWithPreviewCount: Segment = {
          ...res.data,
          lastKnownCount: res.data.lastKnownCount ?? preview.count,
        };
        onCreated(segmentWithPreviewCount);
        setName("");
        setDescription("");
        setPreview(null);
      } else {
        setError(res.error || "Error al guardar");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Error inesperado");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md flex items-center justify-center z-50 animate-fade-in p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-lg flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Crear segmento</h3>
              <p className="text-xs text-slate-500">
                Describí a tu audiencia y la IA arma el filtro
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={pending}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-5">
          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              ¿Cómo es tu audiencia?
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={pending || !!preview}
              placeholder="Ej: clientes que compraron más de 3 veces en los últimos 90 días"
              rows={3}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 resize-none
                focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all
                disabled:bg-slate-50 disabled:cursor-not-allowed"
            />
            {!preview && (
              <div className="mt-2">
                <p className="text-[10px] text-slate-500 mb-1.5">
                  💡 Inspiración:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {EXAMPLE_PROMPTS.map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      onClick={() => setDescription(ex)}
                      disabled={pending}
                      className="text-[10px] px-2 py-1 bg-slate-50 hover:bg-violet-50 hover:text-violet-700 border border-slate-100 hover:border-violet-200 rounded-md transition-all"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Preview from AI */}
          {preview && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 animate-fade-in">
              <div className="flex items-start gap-2">
                <CheckCircle2
                  size={16}
                  className="text-emerald-600 mt-0.5 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                    Filtro armado por la IA
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed mb-2">
                    {preview.summary}
                  </p>
                  <div className="flex items-center gap-2">
                    <Users size={13} className="text-slate-400" />
                    <span className="text-sm font-bold text-slate-900">
                      {preview.count.toLocaleString("es-AR")}
                    </span>
                    <span className="text-xs text-slate-500">
                      usuarios coinciden
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Name input — appears only after preview */}
          {preview && (
            <div className="animate-fade-in">
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                Ponele un nombre
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 80))}
                placeholder="Ej: Clientes VIP recurrentes"
                maxLength={80}
                autoFocus
                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400
                  focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
              />
              <p className="text-[10px] text-slate-400 mt-1 text-right font-mono">
                {name.length}/80
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
              <AlertCircle size={16} />
              <span className="text-xs font-medium">{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={pending}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
          >
            Cancelar
          </button>

          {!preview ? (
            <button
              onClick={handlePreview}
              disabled={pending || !description.trim()}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 text-white
                ${
                  pending || !description.trim()
                    ? "bg-violet-300 cursor-not-allowed"
                    : "bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 shadow-lg shadow-violet-500/25 active:scale-[0.98]"
                }`}
            >
              {pending ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Pensando…
                </>
              ) : (
                <>
                  <Sparkles size={15} />
                  Generar filtro
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleConfirm}
              disabled={pending || !name.trim()}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 text-white
                ${
                  pending || !name.trim()
                    ? "bg-emerald-300 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/25 active:scale-[0.98]"
                }`}
            >
              {pending ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Guardando…
                </>
              ) : (
                <>
                  <CheckCircle2 size={15} />
                  Crear y usar este segmento
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InlineSegmentCreator;
